import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Field, Modal, Select, TextInput } from '../components/ui';
import { Captcha } from '../components/Captcha';
import { SiteFooter } from '../components/Layout';
import { citiesForState, statesForSelect, stateFromGstin, type FoodType } from '@kortex/shared';
import { ApiError, api } from '../lib/api';
import { useAuth, type CaptchaFields } from '../lib/auth';
import { money0 } from '../lib/format';

interface PublicBillingOption {
  id: string;
  intervalUnit: 'MONTH' | 'YEAR';
  intervalCount: number;
  price: number;
  currency: string;
  supportsOneTime: boolean;
  supportsRecurring: boolean;
}

interface PublicPlan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  priceMonthly: number;
  /** Each period on sale. Empty for a plan nobody has priced periods for. */
  billingOptions?: PublicBillingOption[];
  /** Set for a negotiated tier ("Custom"); shown instead of the rupee figure. */
  priceLabel: string | null;
  trialDays: number;
  maxProperties: number;
  maxUsers: number;
  features: string[];
}

interface PublicTestimonial {
  id: string;
  quote: string;
  authorName: string;
  authorTitle: string | null;
  rating: number;
}

interface PublicSiteStat {
  id: string;
  icon: string;
  value: string;
  label: string;
}

/** What the left panel says this product covers. Marketing copy, not routes. */
const HIGHLIGHTS = [
  { icon: '▤', label: 'Billing & POS' },
  { icon: '⌂', label: 'Room Management' },
  { icon: '▦', label: 'Reservations' },
  { icon: '▣', label: 'Inventory' },
  { icon: '✦', label: 'Housekeeping' },
  { icon: '◱', label: 'Reports & Analytics' },
  { icon: '⧉', label: 'Multi-Outlet' },
  { icon: '⋯', label: 'And More' },
] as const;

/** How long a quote stays up before the slider advances on its own. */
const SLIDE_MS = 6000;

/** How long one period runs, so periods of different units compare. */
const monthsIn = (option: PublicBillingOption) =>
  option.intervalUnit === 'YEAR' ? option.intervalCount * 12 : option.intervalCount;

const PERIOD_NAMES: Record<number, string> = {
  1: 'month',
  3: 'quarter',
  6: 'half year',
  12: 'year',
};

/** The adjectival form, for the period cards: "Half-Yearly", not "half year". */
const PERIOD_TITLES: Record<number, string> = {
  1: 'Monthly',
  3: 'Quarterly',
  6: 'Half-Yearly',
  12: 'Yearly',
};

const periodName = (option: PublicBillingOption) =>
  PERIOD_NAMES[monthsIn(option)] ?? `${monthsIn(option)} months`;

const periodTitle = (option: PublicBillingOption) =>
  PERIOD_TITLES[monthsIn(option)] ?? `Every ${monthsIn(option)} months`;

/** A period costs this much per month once its length is spread across it. */
const monthlyRate = (option: PublicBillingOption) =>
  Math.round(option.price / monthsIn(option));

/**
 * How much longer commitment saves, against this plan's own monthly rate.
 *
 * Computed rather than configured, so the badge can never claim a discount the
 * prices do not actually give. Returns 0 when there is no monthly period to
 * compare against, or when the longer period is not in fact cheaper - both of
 * which simply leave the badge off.
 */
function savingPercent(option: PublicBillingOption, periods: PublicBillingOption[]): number {
  const monthly = periods.find((candidate) => monthsIn(candidate) === 1);
  if (!monthly || monthly.price <= 0 || monthsIn(option) === 1) return 0;
  const saved = Math.round((1 - monthlyRate(option) / monthly.price) * 100);
  return saved > 0 ? saved : 0;
}

/**
 * What the row leads with: the shortest period actually on sale.
 *
 * Read from the billing options rather than the plan's own `priceMonthly`,
 * because that headline figure and the price a hotel is really charged can
 * drift apart once somebody edits one and not the other. Falls back to it only
 * for a plan that has no periods configured yet.
 */
function headlinePrice(plan: PublicPlan): { amount: string; sub: string | null } {
  if (plan.priceLabel) return { amount: plan.priceLabel, sub: 'Tailored to your needs' };

  const shortest = [...(plan.billingOptions ?? [])].sort((a, b) => monthsIn(a) - monthsIn(b))[0];
  const price = shortest ? shortest.price : plan.priceMonthly;
  if (price <= 0) {
    return {
      amount: '₹0',
      sub: plan.trialDays > 0 ? `for ${plan.trialDays} days` : null,
    };
  }
  return {
    amount: money0(price),
    sub: `per ${shortest ? periodName(shortest) : 'month'}`,
  };
}

/**
 * What a tier gives you, in the order a reader wants it.
 *
 * The two limits come first because they are the figures people actually
 * compare tiers on, and they are always present. Whatever a superadmin has
 * listed under the plan's own features follows.
 */
const includedIn = (plan: PublicPlan): string[] => [
  `Up to ${plan.maxProperties} ${plan.maxProperties === 1 ? 'property' : 'properties'}`,
  `Up to ${plan.maxUsers} ${plan.maxUsers === 1 ? 'user' : 'users'}`,
  ...plan.features,
];

/**
 * A glyph and a tint per tier, so the rows are told apart at a glance.
 *
 * Keyed by plan code with a fallback, because plans are data: a tier added
 * from the console gets the neutral badge rather than no badge at all.
 */
const PLAN_BADGES: Record<string, { glyph: string; tone: string }> = {
  FREE: { glyph: '✦', tone: 'brand' },
  STARTER: { glyph: '⌂', tone: 'blue' },
  GROWTH: { glyph: '◱', tone: 'green' },
  PROFESSIONAL: { glyph: '♛', tone: 'violet' },
  ENTERPRISE: { glyph: '▦', tone: 'amber' },
};

const planBadge = (code: string) => PLAN_BADGES[code] ?? { glyph: '◆', tone: 'brand' };

/** Where "Talk to Sales" goes - the same address the page footer points at. */
const SALES_URL = 'https://www.europiumsolutions.com/contact-us/';


const BUSINESS_FIELDS = [
  { name: 'name', label: 'Hotel name', placeholder: 'Sunrise Residency', required: true },
  {
    name: 'legalName',
    label: 'Registered legal name',
    placeholder: 'Sunrise Hospitality Pvt Ltd',
    required: false,
  },
  {
    name: 'email',
    label: 'Hotel email',
    type: 'email',
    placeholder: 'contact@sunrise.in',
    required: true,
  },
  { name: 'phone', label: 'Hotel phone', placeholder: '+91 80 4000 1234', required: true },
  { name: 'gstin', label: 'GSTIN', placeholder: '29AABCK1234M1Z5', required: false },
  {
    name: 'fssai',
    label: 'FSSAI licence',
    placeholder: '12345678901234',
    required: true,
  },
  {
    name: 'address',
    label: 'Address',
    placeholder: '14 MG Road, Bengaluru 560001',
    required: true,
    wide: true,
  },
] as const;

const OTHER_CITY = '__other__';

interface OtpChallenge {
  /** False when the platform has no outgoing mail, so nothing can be sent. */
  required: boolean;
  sent: boolean;
  reason?: string;
  expiresInMinutes: number;
  /** When the code stops working; null when none was sent. */
  expiresAt: string | null;
}

/**
 * Seconds left until `until`, counting down once a second.
 *
 * Anchored to the instant rather than to a number of seconds held in state,
 * so it stays right across a tab that was backgrounded - browsers throttle
 * timers in the background, and a counter that decrements itself would come
 * back minutes out and claim time the code no longer has.
 */
const secondsLeft = (until: string | null | undefined): number => {
  if (!until) return 0;
  const at = new Date(until).getTime();
  if (Number.isNaN(at)) return 0;
  return Math.max(0, Math.ceil((at - Date.now()) / 1000));
};

function useSecondsUntil(until: string | null | undefined): number {
  const [seconds, setSeconds] = useState(() => secondsLeft(until));

  useEffect(() => {
    setSeconds(secondsLeft(until));
    if (!until) return;
    const timer = setInterval(() => setSeconds(secondsLeft(until)), 1000);
    return () => clearInterval(timer);
  }, [until]);

  return seconds;
}

/** `9:58`, for a countdown somebody is watching. */
const mmss = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/** docs/platform/coupons.md - a client-side nicety only. The server
 *  re-validates and redeems authoritatively at submit time regardless of
 *  whether "Apply" was ever clicked. */
interface CouponCheck {
  valid: boolean;
  benefitSummary?: string;
  message: string;
}

type FieldErrors = Record<string, string>;

const EMAIL_TAKEN = 'That email address is already registered. Sign in instead, or use another.';

/** Enough to be worth asking about; whether it is a real address is the server's call. */
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** How long to wait after the last keystroke before asking whether an email is taken. */
const EMAIL_CHECK_DELAY_MS = 500;

/**
 * Public self-registration.
 *
 * Every business field the tax invoice needs is collected up front, except
 * GSTIN: a property below the GST registration threshold, or one that
 * hasn't registered yet, can sign up without one and add it later from
 * Settings. Everything else stays required, so a hotel cannot end up inside
 * the product unable to raise a compliant bill.
 */
export function Register() {
  const { status, register } = useAuth();
  const [business, setBusiness] = useState<Record<string, string>>({});
  const [owner, setOwner] = useState<Record<string, string>>({});
  const [planCode, setPlanCode] = useState('FREE');
  /** The period picked inside the open accordion, if any. */
  const [billingOptionId, setBillingOptionId] = useState<string | null>(null);
  /** Whether that period should renew on its own once the trial ends. */
  const [autoRenew, setAutoRenew] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);
  const [comparing, setComparing] = useState(false);
  const [captcha, setCaptcha] = useState<CaptchaFields>({});
  const [captchaToken, setCaptchaToken] = useState(0);
  const [cityMode, setCityMode] = useState<'list' | 'other'>('list');
  const [serves, setServes] = useState({ veg: false, nonVeg: false });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [otp, setOtp] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  /** The server refused the code in hand, so waiting out its timer helps nobody. */
  const [otpRejected, setOtpRejected] = useState(false);
  /*
   * How long the code in somebody's inbox has left.
   *
   * While it runs, sending another is refused: the one they have still works,
   * and a second mail to the same address arriving at the same speed answers
   * nothing. It also stops a run of presses burning the registration
   * allowance, which is shared with the rest of the sign-up and would lock
   * them out of finishing at all.
   */
  const codeValidFor = useSecondsUntil(challenge?.expiresAt);
  /** Send again waits out a live code, unless the server has already refused it. */
  const resendLocked = codeValidFor > 0 && !otpRejected;
  const [otpBusy, setOtpBusy] = useState(false);
  const otpRowRef = useRef<HTMLDivElement>(null);
  /** Nothing sent yet, or the last code is spent: sending is the move. */
  const sendIsNext = challenge?.required !== false && !resendLocked;
  /** A code is on its way and still good: typing it is the move. */
  const awaitingCode = Boolean(challenge?.sent) && codeValidFor > 0 && !otpRejected;

  // Once the mail has gone, put the cursor in the box it is meant for, so the
  // next thing they do is type rather than wonder whether anything happened.
  useEffect(() => {
    if (challenge?.sent) otpRowRef.current?.querySelector('input')?.focus();
  }, [challenge?.expiresAt, challenge?.sent]);
  const [couponCode, setCouponCode] = useState('');
  const [couponCheck, setCouponCheck] = useState<CouponCheck | null>(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  /** The sign-in email the server last said already has an account. */
  const [takenEmail, setTakenEmail] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1);
  const formTopRef = useRef<HTMLDivElement>(null);

  const plans = useQuery({
    queryKey: ['public-plans'],
    queryFn: () => api.get<PublicPlan[]>('/api/public/plans'),
  });

  const testimonials = useQuery({
    queryKey: ['public-testimonials'],
    queryFn: () => api.get<PublicTestimonial[]>('/api/public/testimonials'),
  });

  const siteStats = useQuery({
    queryKey: ['public-site-stats'],
    queryFn: () => api.get<PublicSiteStat[]>('/api/public/site-stats'),
  });

  const quotes = testimonials.data ?? [];
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [slidePaused, setSlidePaused] = useState(false);

  // Advance on its own when a superadmin has written more than one, unless the
  // reader is hovering the card or has just used the dots - taking the text
  // away mid-sentence because a timer fired is the usual complaint about
  // carousels. The modulo on read (rather than clamping here) keeps the index
  // valid if the list shrinks between renders.
  useEffect(() => {
    if (quotes.length < 2 || slidePaused) return;
    const timer = setInterval(() => setQuoteIndex((i) => i + 1), SLIDE_MS);
    return () => clearInterval(timer);
  }, [quotes.length, slidePaused]);

  const activeQuote = quotes.length > 0 ? quoteIndex % quotes.length : 0;
  const quote = quotes.length > 0 ? quotes[activeQuote] : null;
  const stats = siteStats.data ?? [];

  const ownerEmail = (owner.email ?? '').trim().toLowerCase();

  /**
   * Asks whether somebody already signs in with this address, and records the
   * answer against it. Advisory: a request that fails says nothing, because the
   * server refuses a taken address at submit whatever happened here.
   */
  const lookUpOwnerEmail = async (email: string): Promise<boolean> => {
    if (!LOOKS_LIKE_EMAIL.test(email)) return false;
    try {
      const { available } = await api.post<{ available: boolean }>(
        '/api/public/register/check-email',
        { email },
      );
      setTakenEmail((current) => (available ? (current === email ? null : current) : email));
      return !available;
    } catch {
      return false;
    }
  };

  // Check as they type rather than three steps later, once they pause. A
  // stale answer for an address already edited away is dropped by the cleanup.
  useEffect(() => {
    if (!LOOKS_LIKE_EMAIL.test(ownerEmail)) return;
    let current = true;
    const timer = setTimeout(() => {
      void lookUpOwnerEmail(ownerEmail).then((taken) => {
        if (current && taken) setErrors((prev) => ({ ...prev, 'owner.email': EMAIL_TAKEN }));
      });
    }, EMAIL_CHECK_DELAY_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
    // lookUpOwnerEmail only closes over state setters, which are stable.
  }, [ownerEmail]);

  /**
   * What each step needs before it will let you move on.
   *
   * Presence checks only - shape (a real email, a strong enough password, a
   * GSTIN whose state matches) stays with the server, which validates the whole
   * payload at submit regardless of what happened here. The point is to catch
   * an empty box on the step you can still see, rather than three steps later.
   */
  const checkStep = (target: number): FieldErrors => {
    const found: FieldErrors = {};
    if (target === 1) {
      for (const field of BUSINESS_FIELDS) {
        if (field.required && !(business[field.name] ?? '').trim()) {
          found[`business.${field.name}`] = `${field.label} is required`;
        }
      }
      if (!(business.stateCode ?? '').trim()) found['business.stateCode'] = 'Choose a state';
      if (!(business.city ?? '').trim()) found['business.city'] = 'Choose a city';
      if (!(business.pincode ?? '').trim()) found['business.pincode'] = 'PIN code is required';
      if (!foodType) found['business.foodType'] = 'Tick at least one';
    }
    if (target === 2) {
      if (!(owner.name ?? '').trim()) found['owner.name'] = 'Your name is required';
      if (!(owner.email ?? '').trim()) found['owner.email'] = 'A sign-in email is required';
      else if (takenEmail === ownerEmail) found['owner.email'] = EMAIL_TAKEN;
      if (!(owner.password ?? '').trim()) found['owner.password'] = 'Choose a password';
      if (!planCode) found['planCode'] = 'Pick a plan to continue';
    }
    return found;
  };

  /** Which step owns a field, so a rejected submit can reopen the right one. */
  const stepOfField = (path: string): number => {
    if (path.startsWith('business.')) return 1;
    if (path.startsWith('owner.') || path === 'planCode' || path === 'couponCode') return 2;
    return 3;
  };

  const steps = [
    { n: 1, label: 'Business Details' },
    { n: 2, label: 'Account Setup' },
    { n: 3, label: 'Verification' },
  ];
  // A step counts as done once it has been satisfied, so the trail fills in
  // behind you and you can click back to anything already cleared.
  const stepDone = (n: number) => n < step && Object.keys(checkStep(n)).length === 0;

  const goToStep = (target: number) => {
    setFormError(null);
    setStep(target);
    // Back to the top of the form: the next step starts where the last one
    // ended otherwise, which on a long section means opening halfway down it.
    formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goNext = async () => {
    const found = checkStep(step);
    // Pressing Continue inside the typing delay would otherwise outrun the
    // check above, and the answer would only turn up on the last step.
    if (step === 2 && !found['owner.email'] && (await lookUpOwnerEmail(ownerEmail))) {
      found['owner.email'] = EMAIL_TAKEN;
    }
    if (Object.keys(found).length > 0) {
      setErrors((prev) => ({ ...prev, ...found }));
      setFormError('Fill in the highlighted fields to continue.');
      return;
    }
    goToStep(Math.min(step + 1, steps.length));
  };

  if (status === 'authenticated') return <Navigate to="/" replace />;

  const set = (target: 'business' | 'owner', field: string, value: string) => {
    const update = target === 'business' ? setBusiness : setOwner;
    update((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${target}.${field}`];
      return next;
    });
  };

  /**
   * Asks for a code before the form is submitted.
   *
   * The answer says whether verification is even switched on: on a platform
   * with no mail server the API cannot send anything, and says so, rather than
   * leaving somebody waiting for a mail that will never arrive.
   */
  const requestOtp = async () => {
    const email = (business.email ?? '').trim();
    if (!email) {
      setErrors((prev) => ({ ...prev, 'business.email': 'Enter the hotel email first' }));
      return;
    }
    setOtpBusy(true);
    setFormError(null);
    try {
      setChallenge(
        await api.post<OtpChallenge>('/api/public/register/otp', {
          email,
          hotelName: (business.name ?? '').trim(),
        }),
      );
      setOtpRejected(false);
      setOtp('');
      setErrors((prev) => {
        const next = { ...prev };
        delete next['otp'];
        return next;
      });
    } catch (caught) {
      // A rejected address belongs to the hotel email on step 1. Shown here as
      // the server's bare "Check the highlighted fields", it pointed at nothing:
      // the field is two steps back and was never marked.
      const emailIssue =
        caught instanceof ApiError
          ? caught.details?.find((detail) => detail.path === 'email')
          : undefined;
      if (emailIssue) {
        setErrors((prev) => ({ ...prev, 'business.email': emailIssue.message }));
        goToStep(1);
        setFormError('Fix the hotel email, then come back to verify it.');
        return;
      }
      setFormError(
        caught instanceof ApiError ? caught.message : 'Could not send the code. Try again.',
      );
    } finally {
      setOtpBusy(false);
    }
  };

  /** Validates only - never redeems (docs/platform/coupons.md). Redemption
   *  happens server-side, only once the rest of registration succeeds. */
  const applyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;
    setCouponBusy(true);
    setFormError(null);
    try {
      setCouponCheck(
        await api.post<CouponCheck>('/api/public/register/coupon/validate', { code, planCode }),
      );
    } catch (caught) {
      setCouponCheck({
        valid: false,
        message: caught instanceof ApiError ? caught.message : 'Could not check that code. Try again.',
      });
    } finally {
      setCouponBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    // Enter inside a field submits the form, so on the earlier steps that has
    // to mean "next", not "register me with half the answers".
    if (step < steps.length) {
      void goNext();
      return;
    }
    setBusy(true);
    setFormError(null);
    setErrors({});

    try {
      await register({
        business: { ...business, ...(foodType ? { foodType } : {}) },
        owner,
        planCode,
        billingOptionId: billingOptionId ?? undefined,
        autoRenew: billingOptionId ? autoRenew : undefined,
        couponCode: couponCode.trim() || undefined,
        ...captcha,
        acceptTerms,
        otp: otp.trim() || undefined,
      });
    } catch (caught) {
      if (caught instanceof ApiError && Array.isArray(caught.details)) {
        const mapped: FieldErrors = {};
        for (const detail of caught.details as Array<{ path: string; message: string }>) {
          mapped[detail.path] = detail.message;
        }
        setErrors(mapped);
        // Reopen whichever step holds the first complaint. Without this a
        // rejected hotel name is reported on the verification step, where the
        // field it refers to is not even on screen.
        const earliest = Object.keys(mapped)
          .map(stepOfField)
          .sort((a, b) => a - b)[0];
        if (earliest && earliest < step) goToStep(earliest);
        setFormError('Check the highlighted fields.');
      } else if (caught instanceof ApiError && caught.code.startsWith('OTP_')) {
        // Wrong, expired or out of attempts: say so on the code box, and let
        // them ask for a fresh one now rather than when the old timer runs out.
        setErrors({ otp: caught.message });
        setOtpRejected(true);
        setFormError(caught.message);
      } else {
        setFormError(
          caught instanceof ApiError
            ? caught.message
            : 'Could not complete registration. Try again.',
        );
      }
      // The challenge is spent by the attempt either way.
      setCaptchaToken((token) => token + 1);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Two ticks, three answers.
   *
   * Ticking both boxes is what "both" means, so the form does not need a third
   * option that contradicts the other two - and it leaves nothing selected as a
   * genuinely unanswered question rather than a silent default.
   */
  const foodType: FoodType | null =
    serves.veg && serves.nonVeg ? 'BOTH' : serves.veg ? 'VEG' : serves.nonVeg ? 'NONVEG' : null;

  const selected = plans.data?.find((p) => p.code === planCode);
  const states = statesForSelect();
  const cities = citiesForState(business.stateCode);

  return (
    <div className="auth-shell">
      <aside className="auth-aside auth-aside-register">
        <div className="auth-brand">
          <div className="brand-mark">S</div>
          <div>
            <div className="brand-name">SARTHI</div>
            <div className="brand-strap">
              Smart Automated Retail Transaction &amp; Hospitality Infrastructure
            </div>
          </div>
        </div>

        <div className="auth-sectors">
          {['Hotels', 'Restaurants', 'Cafés', 'Resorts', 'Retail'].map((sector) => (
            <span key={sector}>{sector}</span>
          ))}
        </div>

        <div>
          <h1>
            Run Your Hotel Smarter with <em>SARTHI</em>
          </h1>
          <p>
            All your operations in one powerful platform — so you can focus on what matters most,
            your guests.
          </p>
        </div>

        <div className="auth-highlights">
          {HIGHLIGHTS.map((highlight) => (
            <div key={highlight.label}>
              <span className="hl-icon" aria-hidden="true">
                {highlight.icon}
              </span>
              <span>{highlight.label}</span>
            </div>
          ))}
        </div>

        {quote ? (
          <div
            className="auth-slider"
            onMouseEnter={() => setSlidePaused(true)}
            onMouseLeave={() => setSlidePaused(false)}
          >
            <figure className="auth-quote" key={quote.id}>
              <span className="quote-mark" aria-hidden="true">
                “
              </span>
              <blockquote>{quote.quote}</blockquote>
              <figcaption>
                <b>{quote.authorName}</b>
                {quote.authorTitle ? <span>{quote.authorTitle}</span> : null}
                <span className="quote-stars" aria-label={`${quote.rating} out of 5`}>
                  {'★'.repeat(quote.rating)}
                </span>
              </figcaption>
            </figure>

            {quotes.length > 1 ? (
              <div className="slider-dots" role="tablist" aria-label="Testimonials">
                {quotes.map((entry, index) => (
                  <button
                    type="button"
                    key={entry.id}
                    role="tab"
                    aria-selected={index === activeQuote}
                    aria-label={`Testimonial ${index + 1} of ${quotes.length}`}
                    className={index === activeQuote ? 'active' : ''}
                    onClick={() => {
                      setQuoteIndex(index);
                      // Choosing one stops the timer from immediately moving
                      // on; hovering off the card starts it again.
                      setSlidePaused(true);
                    }}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {stats.length > 0 ? (
          <div className="auth-proof">
            {stats.map((stat) => (
              <div key={stat.id}>
                <span className="proof-icon" aria-hidden="true">
                  {stat.icon}
                </span>
                <b>{stat.value}</b>
                <span className="proof-label">{stat.label}</span>
              </div>
            ))}
          </div>
        ) : null}

        <div className="auth-aside-foot">
          Already registered? <Link to="/sign-in">Sign in</Link>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-card wide">
          {/* Anchor for the jump between steps. Above the heading, not at the
              top of the form, so arriving on a step shows which step it is. */}
          <div ref={formTopRef} />
          <div className="auth-topline">
            Already have an account? <Link to="/sign-in">Sign in</Link>
          </div>

          <h2>Register your hotel</h2>
          <div className="sub">Get started with SARTHI in just a few minutes</div>

          {/* Signposting, not navigation: everything is on one page and the
              server validates the lot at submit. The marker tracks how far
              down the form you have actually filled in. */}
          <ol className="auth-steps" aria-label="Registration steps">
            {steps.map((entry) => {
              const done = stepDone(entry.n);
              return (
                <li
                  key={entry.n}
                  className={`${done ? 'done' : ''}${entry.n === step ? ' current' : ''}`}
                  aria-current={entry.n === step ? 'step' : undefined}
                >
                  {/* Only a completed step is clickable: jumping forward past
                      an unanswered one is what the Continue button guards. */}
                  <button
                    type="button"
                    className="step-dot"
                    disabled={!done}
                    onClick={() => goToStep(entry.n)}
                    aria-label={`${entry.label}${done ? ' — done, go back to it' : ''}`}
                  >
                    {done ? '✓' : entry.n}
                  </button>
                  <span className="step-label">{entry.label}</span>
                </li>
              );
            })}
          </ol>

          <div className="sub" style={{ marginBottom: 4 }}>
            {step === 1
              ? "These details appear on every tax invoice you raise, so they are all required — except your GSTIN, if you don't have one yet. You can add it later from Settings."
              : step === 2
                ? 'The account you will sign in with, and the plan to start on. Both can be changed later — the plan from Settings, the password from your own profile.'
                : 'Last step. We check the hotel email reaches you, then create everything at once.'}
          </div>

          {/*
            noValidate on purpose. The steps that are not on screen stay
            mounted (so the captcha is not refetched and nothing typed is lost)
            but hidden, and a browser refuses to submit a form holding a
            `required` control it cannot focus - it would fail silently with
            nothing on screen to explain why. `checkStep` does that job here,
            and the server revalidates everything regardless.
          */}
          <form onSubmit={submit} className="col gap-16" noValidate>
            {formError ? <div className="form-error">{formError}</div> : null}

            <section className="card" hidden={step !== 1}>
              <div className="card-head">
                <h3>Business profile</h3>
                <span className="chip">Required</span>
              </div>
              <div className="card-body">
                <div className="form-grid">
                  {BUSINESS_FIELDS.map((field) => (
                    <div
                      key={field.name}
                      className={'wide' in field && field.wide ? 'span2' : undefined}
                    >
                      <TextInput
                        label={field.required ? `${field.label} *` : field.label}
                        type={'type' in field ? field.type : 'text'}
                        placeholder={field.placeholder}
                        required={field.required}
                        value={business[field.name] ?? ''}
                        error={errors[`business.${field.name}`]}
                        onChange={(e) => {
                          set('business', field.name, e.target.value);
                          // The first two digits of a GSTIN are the state, so
                          // fill it in rather than asking twice.
                          if (field.name === 'gstin' && !business.stateCode) {
                            const derived = stateFromGstin(e.target.value.toUpperCase());
                            if (derived) set('business', 'stateCode', derived.code);
                          }
                        }}
                      />
                    </div>
                  ))}

                  <Select
                    label="State *"
                    required
                    value={business.stateCode ?? ''}
                    error={errors['business.stateCode']}
                    onChange={(e) => {
                      set('business', 'stateCode', e.target.value);
                      // The old city belongs to the old state.
                      set('business', 'city', '');
                      setCityMode('list');
                    }}
                  >
                    <option value="">Choose a state…</option>
                    {states.map((state) => (
                      <option key={state.code} value={state.code}>
                        {state.name} ({state.code})
                      </option>
                    ))}
                  </Select>

                  {cityMode === 'list' && cities.length > 0 ? (
                    <Select
                      label="City *"
                      required
                      value={business.city ?? ''}
                      error={errors['business.city']}
                      onChange={(e) => {
                        if (e.target.value === OTHER_CITY) {
                          setCityMode('other');
                          set('business', 'city', '');
                        } else {
                          set('business', 'city', e.target.value);
                        }
                      }}
                    >
                      <option value="">Choose a city…</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                      <option value={OTHER_CITY}>Somewhere else…</option>
                    </Select>
                  ) : (
                    <TextInput
                      label="City *"
                      required
                      placeholder={
                        business.stateCode ? 'Type your town or city' : 'Choose a state first'
                      }
                      disabled={!business.stateCode}
                      hint={
                        cities.length > 0 ? (
                          <button
                            type="button"
                            className="btn ghost sm"
                            onClick={() => {
                              setCityMode('list');
                              set('business', 'city', '');
                            }}
                          >
                            Pick from the list instead
                          </button>
                        ) : undefined
                      }
                      value={business.city ?? ''}
                      error={errors['business.city']}
                      onChange={(e) => set('business', 'city', e.target.value)}
                    />
                  )}

                  <TextInput
                    label="PIN code *"
                    required
                    inputMode="numeric"
                    placeholder="560001"
                    hint="It goes on every invoice you issue"
                    value={business.pincode ?? ''}
                    error={errors['business.pincode']}
                    onChange={(e) =>
                      set('business', 'pincode', e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                  />

                  <Field
                    label="What do you serve? *"
                    className="span2"
                    error={errors['business.foodType']}
                    hint={
                      foodType === 'BOTH'
                        ? 'Your starter menu will cover both, split across the kitchen stations.'
                        : foodType === 'VEG'
                          ? 'You will start with a vegetarian menu.'
                          : foodType === 'NONVEG'
                            ? 'You will start with the non-vegetarian dishes plus the staples every kitchen serves.'
                            : 'Tick both if your kitchen does both.'
                    }
                  >
                    <div className="row wrap gap-16" style={{ paddingTop: 4 }}>
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={serves.veg}
                          onChange={(e) => {
                            setServes((prev) => ({ ...prev, veg: e.target.checked }));
                            setErrors((prev) => {
                              const next = { ...prev };
                              delete next['business.foodType'];
                              return next;
                            });
                          }}
                        />
                        <span>Vegetarian</span>
                      </label>
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={serves.nonVeg}
                          onChange={(e) => {
                            setServes((prev) => ({ ...prev, nonVeg: e.target.checked }));
                            setErrors((prev) => {
                              const next = { ...prev };
                              delete next['business.foodType'];
                              return next;
                            });
                          }}
                        />
                        <span>Non-vegetarian</span>
                      </label>
                    </div>
                  </Field>
                </div>
                <p className="small muted" style={{ marginTop: 10 }}>
                  If you have a GSTIN, the state must match its first two digits — it decides
                  whether a bill carries CGST/SGST or IGST. The FSSAI licence is 14 digits and is
                  printed on every food bill you raise. What you serve decides the starter menu we
                  set up for you - every item arrives unpriced and switched off, ready for you to
                  price.
                </p>
              </div>
            </section>

            <section className="card" hidden={step !== 2}>
              <div className="card-head">
                <h3>Your owner account</h3>
                <span className="small muted">This is what you sign in with</span>
              </div>
              <div className="card-body">
                <div className="form-grid">
                  <TextInput
                    label="Your name *"
                    required
                    value={owner.name ?? ''}
                    error={errors['owner.name']}
                    onChange={(e) => set('owner', 'name', e.target.value)}
                  />
                  <TextInput
                    label="Your phone"
                    value={owner.phone ?? ''}
                    error={errors['owner.phone']}
                    onChange={(e) => set('owner', 'phone', e.target.value)}
                  />
                  <TextInput
                    label="Sign-in email *"
                    type="email"
                    autoComplete="username"
                    required
                    value={owner.email ?? ''}
                    error={errors['owner.email']}
                    onChange={(e) => set('owner', 'email', e.target.value)}
                  />
                  <TextInput
                    label="Password *"
                    type="password"
                    autoComplete="new-password"
                    required
                    hint="At least 12 characters, with upper case, lower case and a number."
                    value={owner.password ?? ''}
                    error={errors['owner.password']}
                    onChange={(e) => set('owner', 'password', e.target.value)}
                  />
                </div>
              </div>
            </section>

            <section className="card" hidden={step !== 2}>
              <div className="card-head">
                <div>
                  <h3>Choose a plan</h3>
                  <span className="xs muted plan-row-sub">
                    Start with a free trial and upgrade or change your plan anytime.
                  </span>
                </div>
                <button
                  type="button"
                  className="btn ghost sm compare-link"
                  onClick={() => setComparing(true)}
                >
                  <span aria-hidden="true">▥</span> Compare plans
                </button>
              </div>
              <div className="card-body col gap-16">
                {plans.isLoading ? (
                  <div className="small muted">Loading plans…</div>
                ) : (
                  <div className="plan-accordion">
                    {(plans.data ?? []).map((plan) => {
                      const periods = plan.billingOptions ?? [];
                      // A zero-price period is not a purchase decision, so the
                      // free tier reads like the quoted one: no picker, no
                      // renewal choice, just what it gives you.
                      const sellable = periods.filter((option) => option.price > 0);
                      const quoted = Boolean(plan.priceLabel) || sellable.length === 0;
                      const open = expandedPlan === plan.code;
                      const badge = planBadge(plan.code);
                      const headline = headlinePrice(plan);
                      const chosen = sellable.find((option) => option.id === billingOptionId);
                      return (
                        <div
                          key={plan.code}
                          className={`plan-row${planCode === plan.code ? ' selected' : ''}${open ? ' open' : ''}`}
                        >
                          <button
                            type="button"
                            className="plan-row-head"
                            aria-expanded={open}
                            onClick={() => {
                              if (open) {
                                setExpandedPlan(null);
                                return;
                              }
                              setExpandedPlan(plan.code);
                              setPlanCode(plan.code);
                              // A period belongs to one plan, so opening
                              // another cannot carry the old choice across.
                              // Opening lands on the shortest period rather
                              // than on nothing, which is the one most people
                              // want and the cheapest to commit to.
                              const first = sellable[0] ?? null;
                              setBillingOptionId(first?.id ?? null);
                              if (!first?.supportsRecurring) setAutoRenew(false);
                            }}
                          >
                            <span className="plan-radio" aria-hidden="true" />
                            <span className={`plan-icon tone-${badge.tone}`} aria-hidden="true">
                              {badge.glyph}
                            </span>
                            <span className="grow">
                              <span className="plan-title">
                                <b>{plan.name}</b>
                                {plan.trialDays > 0 ? (
                                  <span className="chip ok">{plan.trialDays}-day trial</span>
                                ) : null}
                              </span>
                              {plan.description ? (
                                <span className="xs muted plan-row-sub">{plan.description}</span>
                              ) : null}
                            </span>
                            <span className="plan-row-price">
                              <b>{headline.amount}</b>
                              {headline.sub ? (
                                <span className="plan-per">{headline.sub}</span>
                              ) : null}
                            </span>
                            <span className="plan-caret" aria-hidden="true">
                              {open ? '⌃' : '⌄'}
                            </span>
                          </button>

                          {open ? (
                            <div className="plan-row-body">
                              <div className="plan-body-cols">
                                <div className="plan-periods">
                                  {quoted ? (
                                    <p className="small muted">
                                      {plan.priceLabel
                                        ? 'Priced for what you need. Start on any plan today and we will size this with you — nothing here is charged during your trial.'
                                        : 'Free for the whole trial period, with no card and no billing period to choose. Move to a paid plan whenever you are ready.'}
                                    </p>
                                  ) : (
                                    <>
                                      <div className="b">Select billing period</div>
                                      <div className="xs muted">
                                        Choose the plan that works best for you after the trial
                                        ends.
                                      </div>
                                      <div className="period-cards">
                                        {sellable.map((option) => {
                                          const months = monthsIn(option);
                                          const saved = savingPercent(option, sellable);
                                          return (
                                            <button
                                              type="button"
                                              key={option.id}
                                              className={`period-card${billingOptionId === option.id ? ' selected' : ''}`}
                                              aria-pressed={billingOptionId === option.id}
                                              onClick={() => {
                                                setPlanCode(plan.code);
                                                setBillingOptionId(option.id);
                                                // A period that cannot carry a
                                                // mandate must not inherit the
                                                // renewal choice from one that
                                                // could.
                                                if (!option.supportsRecurring) setAutoRenew(false);
                                              }}
                                            >
                                              <span className="period-card-head">
                                                <span className="period-radio" aria-hidden="true" />
                                                <span className="period-card-name">
                                                  {periodTitle(option)}
                                                </span>
                                              </span>
                                              <span className="period-card-price">
                                                {money0(option.price)}
                                              </span>
                                              <span className="xs muted">
                                                {months === 1 ? 'per month' : `for ${months} months`}
                                              </span>
                                              {saved > 0 ? (
                                                <span className="chip ok">Save {saved}%</span>
                                              ) : null}
                                              {months > 1 ? (
                                                <span className="xs muted period-card-rate">
                                                  {money0(monthlyRate(option))} per month
                                                </span>
                                              ) : null}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </>
                                  )}
                                </div>

                                <div className="plan-included">
                                  <div className="b">What&rsquo;s included</div>
                                  <ul className="plan-features">
                                    {includedIn(plan).map((line) => (
                                      <li key={line}>{line}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>

                              {quoted ? null : (
                                <>
                                  <label
                                    className={`renew-toggle${chosen?.supportsRecurring ? '' : ' disabled'}`}
                                  >
                                    <input
                                      type="checkbox"
                                      className="switch"
                                      checked={Boolean(autoRenew && chosen?.supportsRecurring)}
                                      disabled={!chosen?.supportsRecurring}
                                      onChange={(e) => setAutoRenew(e.target.checked)}
                                    />
                                    <span className="grow">
                                      <b>Automatically renew my subscription</b>
                                      <span className="xs muted plan-row-sub">
                                        {chosen?.supportsRecurring
                                          ? autoRenew
                                            ? `We will set up a recurring payment with Cashfree, so ${money0(chosen.price)} is collected every ${periodName(chosen)} when the trial ends. Nothing is charged today and you can cancel at any time.`
                                            : 'Your subscription will automatically renew at the end of the billing period. You can cancel anytime.'
                                          : 'This period is paid one period at a time — we will ask you before anything is charged again.'}
                                      </span>
                                    </span>
                                  </label>
                                </>
                              )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="plan-help">
                  <span className="plan-icon tone-amber" aria-hidden="true">
                    ✦
                  </span>
                  <span className="grow">
                    <b>Not sure which plan is right for you?</b>
                    <span className="xs muted plan-row-sub">
                      You can start with a free trial and change or upgrade your plan anytime.
                    </span>
                  </span>
                  <a className="btn" href={SALES_URL} target="_blank" rel="noreferrer">
                    Talk to Sales
                  </a>
                </div>
              </div>
            </section>

            <section className="card" hidden={step !== 2}>
              <div className="card-head">
                <h3>Have a promotional code?</h3>
                <span className="small muted">Optional</span>
              </div>
              <div className="card-body col gap-16">
                <div className="row gap-6 wrap" style={{ alignItems: 'flex-end' }}>
                  <TextInput
                    label="Promotional code"
                    placeholder="WELCOME3M"
                    value={couponCode}
                    error={errors['couponCode']}
                    disabled={busy}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponCheck(null);
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next['couponCode'];
                        return next;
                      });
                    }}
                  />
                  <button
                    type="button"
                    className="btn"
                    disabled={busy || couponBusy || !couponCode.trim()}
                    onClick={() => void applyCoupon()}
                  >
                    {couponBusy ? 'Checking…' : 'Apply'}
                  </button>
                </div>
                {couponCheck?.valid ? (
                  <p className="small ok">✓ {couponCheck.benefitSummary} applied.</p>
                ) : couponCheck ? (
                  <p className="small warn">{couponCheck.message}</p>
                ) : null}
              </div>
            </section>

            <section className="card" hidden={step !== 3}>
              <div className="card-head">
                <h3>Verify your email</h3>
                <span className="small muted">So we know the address reaches you</span>
              </div>
              <div className="card-body col gap-16">
                {/*
                 * Said before the field rather than under it.
                 *
                 * Nobody can finish this step without pressing the button,
                 * and as an 11px muted hint below the box this was the
                 * quietest thing on the card - people sat looking for a code
                 * that had never been sent. It says what to do, then the
                 * controls to do it with, then confirms when it is done.
                 */}
                {challenge?.required === false ? null : (
                  <p
                    // Keyed by the state it describes, so each change of state
                    // replays the entrance rather than swapping text silently.
                    key={`${Boolean(challenge?.sent)}-${otpRejected}-${resendLocked}`}
                    className={`form-note attention ${
                      challenge?.sent ? (resendLocked ? 'ok' : 'warn') : 'info'
                    }`}
                  >
                    {challenge?.sent ? (
                      otpRejected ? (
                        <>
                          <b>That code was not accepted.</b> Check the digits, or send another and
                          we will email a fresh one to {business.email}.
                        </>
                      ) : codeValidFor > 0 ? (
                        <>
                          <b>Code sent to {business.email}.</b> Type the six digits below — they
                          work for another <b>{mmss(codeValidFor)}</b>.
                        </>
                      ) : (
                        /* Said plainly rather than left to be discovered by
                           typing a code that no longer works. */
                        <>
                          <b>That code has expired.</b> Send another and we will email a fresh one
                          to {business.email}.
                        </>
                      )
                    ) : (
                      <>
                        <b>Press “Click Here to Get Code” to start.</b> We send six digits to{' '}
                        {business.email || 'your hotel email'}; type them in below to finish
                        registering.
                      </>
                    )}
                  </p>
                )}
                <div className="otp-row" ref={otpRowRef}>
                  <TextInput
                    className={awaitingCode ? 'awaiting-code' : undefined}
                    label="Code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    value={otp}
                    error={errors['otp']}
                    disabled={busy || challenge?.required === false}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  />
                  {/*
                   * Primary until it has been used. It is the one thing that
                   * has to happen on this card, and a quiet button beside a
                   * box people expect to type into reads as the lesser of the
                   * two - which is the wrong way round here.
                   */}
                  {/*
                   * Refused while the code they already have still works, and
                   * the button says how long that is rather than sitting grey
                   * with no reason - a disabled control that will not explain
                   * itself is read as broken, and the next thing somebody
                   * does is reload and lose the form.
                   */}
                  <button
                    type="button"
                    className={`btn ${challenge?.sent ? '' : 'primary'}${
                      sendIsNext && !otpBusy && !busy ? ' nudge' : ''
                    }`}
                    disabled={
                      busy || otpBusy || challenge?.required === false || resendLocked
                    }
                    title={
                      resendLocked
                        ? 'The code already in your inbox still works. You can ask for another once it expires.'
                        : undefined
                    }
                    onClick={() => void requestOtp()}
                  >
                    {sendIsNext && !otpBusy ? (
                      <span className="nudge-icon" aria-hidden="true">
                        ✉
                      </span>
                    ) : null}
                    {otpBusy
                      ? 'Sending…'
                      : resendLocked
                        ? `Send again in ${mmss(codeValidFor)}`
                        : challenge
                          ? 'Send again'
                          : 'Click Here to Get Code'}
                  </button>
                </div>
                {/*
                 * Where a first message from an unknown sender usually lands.
                 * Worth saying before they ask for another code that will go
                 * to the same place - and worth asking them to mark it safe
                 * now, because every later message from us (a reset link, a
                 * renewal receipt) follows the same route.
                 */}
                {challenge?.sent ? (
                  <p className="xs muted">
                    Not in your inbox? Check the junk or spam folder — a first message from a new
                    sender often lands there. Mark it <b>Not junk</b> so your invoices, receipts
                    and password reset links reach you later.
                  </p>
                ) : null}
                {challenge && challenge.required === false ? (
                  <p className="form-note">
                    Email verification is switched off on this server, so you can register without a
                    code.
                  </p>
                ) : challenge && !challenge.sent ? (
                  <p className="form-note warn">
                    <b>We could not send it{challenge.reason ? `: ${challenge.reason}` : ''}.</b>{' '}
                    Mail is switched on for this server, so registration needs the code — ask
                    whoever runs this SARTHI to check Platform → Outgoing Mail, then try again.
                  </p>
                ) : null}
              </div>
            </section>

            <section className="card" hidden={step !== 3}>
              <div className="card-body">
                <Captcha
                  value={captcha}
                  onChange={setCaptcha}
                  error={errors['captcha']}
                  disabled={busy}
                  reloadToken={captchaToken}
                />
              </div>
            </section>

            <label className="accept-terms" hidden={step !== 3}>
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => {
                  setAcceptTerms(e.target.checked);
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next['acceptTerms'];
                    return next;
                  });
                }}
              />
              <span>
                I accept the{' '}
                {/*
                  Opens in a new tab, so a half-filled form is not lost to
                  reading it. The click is stopped from reaching the label,
                  which would otherwise tick the box on the way past - agreeing
                  on somebody's behalf because they wanted to read first.
                */}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  terms and conditions
                </a>
                .
              </span>
            </label>
            {errors['acceptTerms'] && step === 3 ? (
              <div className="field-error" style={{ marginTop: -6 }}>
                {errors['acceptTerms']}
              </div>
            ) : null}

            <div className="step-nav">
              {step > 1 ? (
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => goToStep(step - 1)}
                >
                  ← Back
                </button>
              ) : (
                <span />
              )}

              {step < steps.length ? (
                <button type="button" className="btn primary lg" onClick={() => void goNext()}>
                  Continue →
                </button>
              ) : (
                <button className="btn primary lg" type="submit" disabled={busy || !acceptTerms}>
                  {busy
                    ? 'Creating your hotel…'
                    : selected && selected.trialDays > 0
                      ? `Start my ${selected.trialDays}-day free trial`
                      : 'Create my hotel'}
                </button>
              )}
            </div>

            {step === 3 ? (
              <p className="small muted center">
                You also agree to keep your GSTIN and business details accurate — they appear on
                the invoices you issue to guests.
              </p>
            ) : (
              <p className="small muted center">
                Step {step} of {steps.length} — nothing is created until the last one.
              </p>
            )}
          </form>
        </div>

        <SiteFooter />
      </main>

      {comparing ? (
        <Modal title="Compare plans" size="wide" onClose={() => setComparing(false)}>
          <div className="table-wrap">
            <table className="tbl compare-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>From</th>
                  <th>Trial</th>
                  <th>Properties</th>
                  <th>Users</th>
                  <th>Billing periods</th>
                </tr>
              </thead>
              <tbody>
                {(plans.data ?? []).map((plan) => {
                  const headline = headlinePrice(plan);
                  const sellable = (plan.billingOptions ?? []).filter((o) => o.price > 0);
                  return (
                    <tr
                      key={plan.code}
                      className={`clickable${planCode === plan.code ? ' selected' : ''}`}
                      onClick={() => {
                        setPlanCode(plan.code);
                        setExpandedPlan(plan.code);
                        const first = sellable[0] ?? null;
                        setBillingOptionId(first?.id ?? null);
                        if (!first?.supportsRecurring) setAutoRenew(false);
                        setComparing(false);
                      }}
                    >
                      <td>
                        <b>{plan.name}</b>
                        {plan.description ? (
                          <span className="xs muted plan-row-sub">{plan.description}</span>
                        ) : null}
                      </td>
                      <td>
                        <b>{headline.amount}</b>
                        {headline.sub ? (
                          <span className="xs muted plan-row-sub">{headline.sub}</span>
                        ) : null}
                      </td>
                      <td>{plan.trialDays > 0 ? `${plan.trialDays} days` : '—'}</td>
                      <td>{plan.maxProperties}</td>
                      <td>{plan.maxUsers}</td>
                      <td>
                        {sellable.length === 0
                          ? '—'
                          : sellable.map((option) => periodTitle(option)).join(', ')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="small muted" style={{ marginTop: 12 }}>
            Every paid plan can be billed one period at a time or set to renew automatically.
            Pick a row to start on that plan — nothing is charged during your trial.
          </p>
        </Modal>
      ) : null}
    </div>
  );
}
